import fetchTrackMultiline from '@gaia/utils/gaia/fetchTrackMultiLine';
import getAreaPolygon from '@gaia/utils/gaia/getAreaPolygon';
import convertLngLat from '@gaia/utils/mapbox/convertLngLat';
import getBoundsSafely from '@gaia/utils/mapbox/getBoundsSafely';
import getBBox from '@turf/bbox';
import type {Geometry, Point, Position} from 'geojson';
import {LngLat} from 'mapbox-gl';
import {useEffect, useState} from 'react';

import useGetPaddingForSelectionEffect from './useGetPaddingForSelectionEffect';
import {useLatest} from './useLatest';

interface UseFitMapToSelectionEffectOptions {
    interaction: InteractionState;
    sidebarWidth: number;
    selection: SelectionState | undefined;
    tracks: Record<string, Track>;
    waypoints: Record<string, Waypoint>;
    areas: Record<string, Area>;
    knownRoutes: Record<number, KnownRouteTag>;
    map: mapboxgl.Map | undefined;
    project: MapProject;
    controlsExpanded: boolean;
}

type Viewport = {
    center: mapboxgl.LngLat;
    zoom: number;
};

export default function useFitMapToSelectionEffect(options: UseFitMapToSelectionEffectOptions) {
    const [previousViewport, updatePreviousViewport] = useState<Viewport>();

    const [previousSelection, updatePreviousSelection] = useState<SelectionState>();
    const padding = useGetPaddingForSelectionEffect(options.sidebarWidth, options.controlsExpanded);

    const latest = useLatest({options, padding, previousSelection, previousViewport});

    useEffect(() => {
        const {map, selection} = latest.current.options;
        if (!map || !selection || latest.current.options.interaction.type !== 'normal') return;

        const geometryPromise: Promise<Geometry> = getSelectionGeometry(
            latest.current.options,
            latest.current.previousSelection
        );

        // Originally, branches were based on geometry type (e.g. 1st and last branch). Now, every
        // selection type is getting its own branch.
        geometryPromise
            .then((geometry) => {
                if (
                    geometry.type === 'Point' &&
                    (!isInViewport(map, geometry) || map.getZoom() < 8)
                ) {
                    map.flyTo({
                        center: convertLngLat(geometry.coordinates),
                        duration: 250,
                        zoom: Math.max(map.getZoom(), 13),
                    });
                } else if (selection.type === 'osm') {
                    const bbox: BBox2d = getBBox(geometry) as BBox2d;
                    if (latest.current.previousViewport) {
                        map.flyTo({...latest.current.previousViewport, duration: 250});
                        updatePreviousViewport(undefined);
                    } else if (
                        !isInViewport(map, geometry) &&
                        bbox[0] !== Infinity &&
                        bbox[0] !== -Infinity
                    ) {
                        map.fitBounds(bbox, {
                            padding: latest.current.padding,
                            duration: 250,
                        });
                    }
                } else if (selection.type === 'track') {
                    // if the track is not visible in the viewport, then the selection was triggered
                    // by a click in the Saved Items sidebar or similar external source.
                    // In this case, we maximize the amount of screen dedicated to the track
                    if (!isInViewport(map, geometry)) {
                        const bbox: BBox2d = getBBox(geometry) as BBox2d;
                        map.fitBounds(bbox, {
                            padding: latest.current.padding,
                            duration: 250,
                        });
                    }

                    // if the track is visible in the viewport, then we do not adjust the viewport
                } else if (selection.type === 'knownRoute') {
                    if (selection.previousSelection) {
                        updatePreviousViewport({
                            center: convertLngLat(map.getCenter().wrap()),
                            zoom: map.getZoom(),
                        });
                    }

                    let bbox: BBox2d;

                    if (
                        selection.previousSelection &&
                        selection.previousSelection.type === 'osm' &&
                        selection.previousSelection.properties?.type !== 'land'
                    ) {
                        // Extend the bbox to include the source OSM selection
                        const knownRoute = getBBox(geometry) as BBox2d;
                        bbox = extendBBox(
                            knownRoute,
                            LngLat.convert(selection.previousSelection.lngLat)
                        );
                    } else {
                        bbox = getBBox(geometry) as BBox2d;
                    }

                    if (bbox[0] !== Infinity && bbox[0] !== -Infinity) {
                        map.fitBounds(bbox, {
                            padding: latest.current.padding,
                            duration: 250,
                        });
                    }
                } else if (
                    geometry.type !== 'Point' &&
                    (!isContainedInViewport(map, geometry) || map.getZoom() < 10)
                ) {
                    const bbox = getBBox(geometry) as BBox2d;

                    if (bbox[0] !== Infinity && bbox[0] !== -Infinity) {
                        map.fitBounds(bbox, {
                            padding: latest.current.padding,
                            duration: 250,
                        });
                    }
                }
            })
            .catch(() => {
                if (selection.type === 'exploration' && latest.current.previousViewport) {
                    map.flyTo({...latest.current.previousViewport, duration: 250});
                    updatePreviousViewport(undefined);
                }
            });

        updatePreviousSelection(selection);
    }, [
        latest,
        options.selection,
        options.areas,
        options.tracks,
        options.waypoints,
        options.knownRoutes,
    ]);
}

async function getSelectionGeometry(
    {
        interaction,
        selection,
        tracks,
        waypoints,
        areas,
        knownRoutes,
        project,
    }: UseFitMapToSelectionEffectOptions,
    previousSelection: SelectionState | undefined
): Promise<Geometry> {
    if (!selection) return Promise.reject();
    if (selection.type === 'track') {
        const track = tracks[selection.localId];
        return track ? fetchTrackMultiline({track, project, interaction}) : Promise.reject();
    } else if (selection.type === 'waypoint') {
        return waypoints[selection.localId]
            ? waypoints[selection.localId].geometry
            : Promise.reject();
    } else if (selection.type === 'area') {
        const area = areas[selection.localId];
        if (area) {
            return getAreaPolygon(area) ?? Promise.reject();
        }
    } else if (
        selection.type === 'osm' &&
        selection.osmId !== (previousSelection?.type === 'osm' && previousSelection.osmId)
    ) {
        if (selection.properties?.type === 'land') {
            if (selection.properties?.bounds) {
                return {
                    type: 'MultiPoint',
                    coordinates: bboxToPolygon(selection.properties.bounds as BBox2d),
                };
            } else if (
                selection.properties.geometry &&
                selection.properties.geometry.type !== 'Point'
            ) {
                return selection.properties.geometry;
            }
        } else {
            return {
                type: 'Point',
                coordinates: LngLat.convert(selection.lngLat).toArray(),
            } as Point;
        }
    } else if (selection.type === 'knownRoute') {
        const knownRouteTag = knownRoutes[selection.knownRouteId];
        if (knownRouteTag) return knownRouteTag.geometry;
    }
    return Promise.reject();
}

function bboxToPolygon([lng1, lat1, lng2, lat2]: BBox2d): Position[] {
    return [
        [lng1, lat1],
        [lng1, lat2],
        [lng2, lat2],
        [lng2, lat1],
        [lng1, lat1],
    ];
}

function isContainedInViewport(map: mapboxgl.Map, geometry: Geometry) {
    const bounds = getBoundsSafely(map);
    if (!bounds) return true;

    const [[minX, minY], [maxX, maxY]] = bounds.toArray();
    const viewportBBox: BBox2d = [minX, minY, maxX, maxY];

    const geometryBBox = getBBox(geometry) as BBox2d;

    if (!isFinite(geometryBBox[0])) return false;

    return isBBoxContained(geometryBBox, viewportBBox);
}

function isInViewport(map: mapboxgl.Map, geometry: Geometry) {
    const bounds = getBoundsSafely(map);
    if (!bounds) return true;

    const [[minX, minY], [maxX, maxY]] = bounds.toArray();
    const viewportBBox: BBox2d = [minX, minY, maxX, maxY];

    const geometryBBox = getBBox(geometry) as BBox2d;

    if (geometryBBox[0] === Infinity || geometryBBox[0] === -Infinity) {
        return false;
    } else {
        return isBBoxOverlapping(viewportBBox, geometryBBox);
    }
}

function extendBBox([minX, minY, maxX, maxY]: BBox2d, point: mapboxgl.LngLat): BBox2d {
    const newMinX = point.lng < minX ? point.lng : minX;

    const newMaxX = point.lng > maxX ? point.lng : maxX;

    const newMinY = point.lat < minY ? point.lat : minY;

    const newMaxY = point.lat > maxY ? point.lat : maxY;

    return [newMinX, newMinY, newMaxX, newMaxY];
}

function isBBoxOverlapping(
    [aMinX, aMinY, aMaxX, aMaxY]: BBox2d,
    [bMinX, bMinY, bMaxX, bMaxY]: BBox2d
) {
    if (aMinX > aMaxX) aMaxX += 180;
    if (bMinX > bMaxX) bMaxX += 180;

    if (aMinY > aMaxY) aMaxY += 90;
    if (bMinY > bMaxY) bMaxY += 90;

    return aMaxX >= bMinX && bMaxX >= aMinX && aMaxY >= bMinY && bMaxY >= aMinY;
}

export function isBBoxContained(
    [minX, minY, maxX, maxY]: BBox2d,
    [containerMinX, containerMinY, containerMaxX, containerMaxY]: BBox2d
) {
    if (minX > maxX) maxX += 180;
    if (containerMinX > containerMaxX) containerMaxX += 180;

    if (minY > maxY) maxY += 90;
    if (containerMinY > containerMaxY) containerMaxY += 90;

    return (
        maxX <= containerMaxX &&
        minX >= containerMinX &&
        minY >= containerMinY &&
        maxY <= containerMaxY
    );
}
